// src/components/desk/PossibleMatches.jsx — THE possible-matches shelf, for every feed.
//
// The shelf (0055) is the unknown-verdict tier: rows whose data is too coarse or too absent
// to confirm OR deny the active filters. Shelving instead of hiding is the whole point of
// having three verdicts — "we don't know" is an answer, and it is the agent's next call.
//
// ⚠️ WHY THIS IS A SHARED COMPONENT AND NOT A BLOCK INSIDE IntakeTab (2026-07-29).
// It lived inline in IntakeTab behind `!SPECIAL_FEED`, so the Leads category — which owns
// its own container (LeadsFeed) — rendered NO shelf at all. Not suppressed by a rule:
// simply never wired. Measured the day this moved: `posted since 1 July` on scope=leads
// answers 2,975 matched and shelves **14,456**, none of which reached the screen or any
// counter. The server had been computing and sending them the whole time.
//
// A second copy inside LeadsFeed would have been the cheaper edit and the wrong one: the
// shelf's header is a pair of numbers that must describe exactly the set beneath them, and
// this codebase's recurring bug is two implementations of one rule drifting apart. One
// component owns the counting.
//
// ── The unit rule (learned the hard way, same day) ────────────────────────────────────
// Everything here is COUNTED IN CLUSTERS — property identities — because that is the unit
// `unknown_excluded` reports and the unit `unknown_offset` advances in. The shelf briefly
// rendered one card per LISTING while counting clusters: a real page is 60 clusters
// carrying ~192 listings, so the header read "showing 192" of a cluster total and the
// pager would have stopped with most of the shelf unreached. `possibleClusterGroups` below
// is the only place shelf rows become cards, so the two can no longer disagree.

import { t, tLog } from '../../lib/i18n.js'
import { btn } from './primitives.jsx'
import { shelfSurface } from '../../lib/deskAnswerState.js'

export const SHELF_PAGE = 60   // = the RPC's p_unknown_limit default

// Shelf rows → one card per property-identity cluster.
// ⚠️ ORIGIN FIRST: items[0] is the card's face AND what a click opens. Members arrive
// newest-first, and on anything one of our agents reshared the newest listing is OURS — the
// card would then print our own agent as the ad's author. Stable sort, so recency still
// orders within each side. Same rule as LeadsFeed's own grouping, deliberately identical.
export function possibleClusterGroups(rows) {
  const byCluster = new Map()
  for (const l of rows || []) {
    const key = l.cluster_key || l.property_id || l.listing_id
    if (!byCluster.has(key)) byCluster.set(key, [])
    byCluster.get(key).push(l)
  }
  return [...byCluster.entries()].map(([key, members]) => ({
    key: `possible:${key}`,
    items: [...members].sort((a, b) => (a.is_reshare ? 1 : 0) - (b.is_reshare ? 1 : 0)).map(l => ({
      id: `listing:${l.listing_id}`,
      proposal_kind: l.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
      status: l.status, created_at: l.listed_at || null, listing: l, proposed: {}, result: {},
    })),
  }))
}

// `groups`       — everything to render, server-shelved plus anything the caller shelved locally
// `serverGroups` — how many of those came from THIS cursor (server tier). The pager compares
//                  against it, never against `groups`: locally-shelved rows did not come from
//                  the cursor, and counting them would retire the button while server pages
//                  remain.
// `serverTotal`  — the whole-corpus shelf count (`unknown_excluded`), or null if unknown.
// `renderGroup`  — the caller's card. The two feeds pass very different prop sets to
//                  HistoryGroup (leads add the lead overlay strip), and threading fifteen
//                  pass-throughs would make this component a second copy of both callers.
// `header`      — optional heading override, called as header({ total, shown }). The nearby-
//                 street group (Q4) is a THIRD verdict tier with the same counting rules and
//                 the same card machinery, but a different sentence: it must name the street
//                 and say the distance is from its CENTRE. Reusing this component keeps the
//                 "one component owns the counting" rule above intact; a second copy is how
//                 the header and the set beneath it drift apart.
// `failed` / `onRetry` — the FIRST page failed; the caller re-armed it and retries at offset 0.
export function PossibleMatchesShelf({
  groups, serverGroups = 0, serverTotal = null, view = 'card',
  onLoadMore = null, loading = false, exhausted = false, containerRef = null, renderGroup,
  header = null, failed = false, onRetry = null,
}) {
  // 2026-10-23 audit (Desk §5): this returned null until the first cards arrived, so a first page in
  // flight, or one that failed, left the unknown tier counted elsewhere with nothing here.
  const surface = shelfSurface({ groups: groups?.length || 0, loading, failed, serverTotal })
  if (!surface) return null
  if (surface !== 'cards') {
    return (
      <div ref={containerRef} className="mt-6 border-t border-dashed border-border pt-4">
        <p className="text-sm text-muted-foreground">
          {surface === 'loading' ? t('desk_possible_loading') : t('desk_possible_failed')}
          {surface === 'failed' && onRetry && (
            <button type="button" className={`${btn('ghost')} ml-2`} onClick={onRetry}>{t('desk_retry')}</button>
          )}
        </p>
      </div>
    )
  }
  // Sibling of the a539a6b shelf bug (audit 2026-07-27): `serverUnknown ?? local` CHOSE a
  // source — the server shelving 3 while a local filter shelved 2 more read "3" over 5
  // cards. The header counts the server's corpus-wide number PLUS whatever local shelving
  // added on top. Additive, never alternative.
  const localExtra = Math.max(0, groups.length - serverGroups)
  const total = (serverTotal ?? serverGroups) + localExtra
  const canLoadMore = !!onLoadMore && serverTotal != null && serverGroups < serverTotal && !exhausted
  return (
    <div ref={containerRef} className="mt-6 border-t border-dashed border-border pt-4">
      {/* total = the whole corpus. shown = what is actually on screen. These were ONE value
          until 2026-07-29, which made the header claim it was showing all 2,248 over 60. */}
      <p className="text-sm text-muted-foreground mb-3">
        {header ? header({ total, shown: groups.length })
          : tLog('desk_possible_matches_header', { total, shown: groups.length })}
      </p>
      <div className={view === 'list' ? 'space-y-1'
        : view === 'card' ? 'grid gap-3 grid-cols-1 max-w-4xl'
        : 'grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]'}>
        {groups.map(g => renderGroup(g))}
      </div>
      {/* ⚠️ Not an infinite scroller. The shelf is a CALL LIST — "verify this with the
          owner" — and auto-loading a 14,000-row task list is a treadmill, not a workflow.
          The agent asks for the next 60. */}
      {canLoadMore && (
        <div className="mt-3 text-center">
          <button className={btn('ghost')} disabled={loading} onClick={onLoadMore}>
            {loading ? t('desk_possible_loading')
              : tLog('desk_possible_load_more', { loaded: serverGroups, total: serverTotal })}
          </button>
        </div>
      )}
    </div>
  )
}
